import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { ButtonHTMLAttributes, Ref } from 'react'

import { cn } from '@/shared/lib'

export const buttonBase = [
  'inline-flex items-center justify-center whitespace-nowrap px-2.5',
  'cursor-pointer rounded-md font-medium transition-colors',
  '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  'ring-offset-background focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
  'disabled:pointer-events-none',
]

export const buttonVariants = cva(
  buttonBase,

  {
    variants: {
      color: {
        CTA: 'data-[color=CTA]:text-[18px] data-[color=CTA]:leading-[26px] data-[color=CTA]:font-semibold',
        primary: '',
        secondary: '',
      },
      variant: {
        solid: '',
        outline: '',
      },
      size: {
        small: 'typo-label2 min-h-8 py-[7px]',
        medium: 'typo-body2 min-h-10 py-[9px]',
        large: 'typo-body1 min-h-12 py-3',
      },
      shape: {
        pill: 'rounded-full',
        rounded: 'rounded-sm',
      },
      weight: {
        bold: 'font-bold',
        semibold: 'font-semibold',
        medium: 'font-medium',
        normal: 'font-normal',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },

    compoundVariants: [
      {
        variant: 'outline',
        size: 'small',
        className: ['py-[6px]'],
      },
      {
        variant: 'outline',
        size: 'medium',
        className: ['py-[8px]'],
      },
      {
        variant: 'outline',
        size: 'large',
        className: ['py-[11px]'],
      },

      {
        variant: 'solid',
        color: 'CTA',
        className: [
          'bg-purple-500 text-white',
          '[&:not(:disabled)]:hover:bg-purple-500/90',
          'focus-visible:ring-purple-200',
          'disabled:bg-gray-100 disabled:text-gray-500 disabled:[&_svg]:text-gray-500',
        ],
      },
      {
        variant: 'solid',
        color: 'CTA',
        size: 'small',
        className: [
          'data-[color=CTA]:text-[16px] data-[color=CTA]:leading-[24px]',
        ],
      },
      {
        variant: 'solid',
        color: 'primary',
        className: [
          'bg-black text-white',
          '[&:not(:disabled)]:hover:bg-gray-900',
          'disabled:bg-gray-100 disabled:text-gray-500 disabled:[&_svg]:text-gray-500',
        ],
      },
      {
        variant: 'solid',
        color: 'secondary',
        className: [
          'bg-gray-50 text-black',
          '[&:not(:disabled)]:hover:bg-gray-100',
          'disabled:text-gray-300 disabled:[&_svg]:text-gray-300',
        ],
      },

      {
        variant: 'outline',
        color: 'CTA',
        className: [
          'border border-white bg-transparent px-[9px] py-2.5 text-white',
          '[&:not(:disabled)]:hover:bg-white/10',
          'focus-visible:ring-ring',
        ],
      },
      {
        variant: 'outline',
        color: 'primary',
        className: [
          'border border-gray-200 bg-white text-black',
          '[&_svg]:size-4',
          '[&:not(:disabled)]:hover:bg-gray-50',
          'disabled:text-gray-200 disabled:[&_svg]:text-gray-200',
        ],
      },
      {
        variant: 'outline',
        color: 'secondary',
        className: [
          'border border-gray-200 bg-transparent text-black',
          '[&_svg]:size-4',
          '[&:not(:disabled)]:hover:bg-white/10',
          'disabled:text-gray-200 disabled:[&_svg]:text-gray-200',
        ],
      },
    ],
  },
)

export type Props = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    ref?: Ref<HTMLButtonElement>
    asChild?: boolean
  }

export const Button = ({
  className,
  variant = 'solid',
  color = 'primary',
  size = 'medium',
  shape = 'rounded',
  weight = 'medium',
  asChild = false,
  disabled = false,
  fullWidth = false,
  ...props
}: Props) => {
  const Component = asChild ? Slot : 'button'

  return (
    <Component
      className={cn(
        buttonVariants({
          variant,
          color,
          size,
          shape,
          weight,
          fullWidth,
          className,
        }),
      )}
      data-color={color}
      disabled={disabled}
      {...props}
    />
  )
}
